// Based on a combination of:
//  - https://github.com/andyet/SimpleWebRTC/blob/master/simplewebrtc.js
//  - https://github.com/andyet/SimpleWebRTC/blob/master/webrtc.js
//  - https://github.com/otalk/localmedia/blob/master/index.js
//
// Originally, it was going to use `webrtc.js` as a library, the same way that
// `simplewebrtc.js` does, but there is so much duplication between the two files
// that it makes more sense to do it this way.
//
// Also, that library is focused on multiple peers.  Because all of our traffic
// MUST run through the media server (so it can be recorded), we purposely only
// allow a single peer.

import webrtcsupport from 'webrtcsupport';
import getUserMedia from 'getusermedia';
import PeerConnection from 'rtcpeerconnection';
import IceCandidateMessage from './messages/webrtc/ice-candidate';
import AnswerSDPMessage from './messages/webrtc/answer';
import OfferSDPMessage from './messages/webrtc/offer';
import ICEServersRequestMessage from './messages/webrtc/ice-servers-request';

import eventHandler from '../eventBus';

export class LocalMedia {
	constructor(opts) {
		this.config = {
			media: {
				audio: true,
				video: true,
			},
			audioFallback: true,
		};

		for (var item in opts) {
			if (opts.hasOwnProperty(item)) {
				this.config[item] = opts[item];
			}
		}

		this.localStreams = [];
	}

	get streams() {
		return this.localStreams;
	}

	start(cb) {
		var constraints = this.config.media;

		getUserMedia(constraints, (err, stream) => {
			if (!err) {
				this.localStreams.push(stream);
			} else {
				// Fallback for users without a camera to use audio only
				if (this.config.audioFallback && err.name === 'DevicesNotFoundError' && constraints.video !== false) {
					constraints.video = false;
					this.start(constraints, cb);
					return;
				}
			}
			if (cb) {
				return cb(err, stream);
			}
		});
	}

	stop(stream) {
		// Stop a specific one, or stop them all?
		if (stream) {
			stream.getTracks().forEach(function (track) { track.stop(); });
			var idx = this.localStreams.indexOf(stream);
			if (idx > -1) {
				this.localStreams.splice(idx, 1);
			}
		} else {
			this.localStreams.forEach(function (stream) {
				stream.getTracks().forEach(function (track) { track.stop(); });
			});
			this.localStreams = [];
		}
	}

	mute() {
		this.audioEnabled = false;
	}
	unmute() {
		this.audioEnabled = true;
	}
	pauseVideo() {
		this.videoEnabled = false;
	}
	resumeVideo() {
		this.videoEnabled = true;
	}
	pause() {
		this.videoEnabled = false;
		this.audioEnabled = false;
	}
	resume() {
		this.videoEnabled = true;
		this.audioEnabled = true;
	}

	set audioEnabled(bool) {
		this.localStreams.forEach(function (stream) {
			stream.getAudioTracks().forEach(function (track) {
				track.enabled = !!bool;
			});
		});
	}

	set videoEnabled(bool) {
		this.localStreams.forEach(function (stream) {
			stream.getVideoTracks().forEach(function (track) {
				track.enabled = !!bool;
			});
		});
	}

	get audioEnabled() {
		var enabled = true;
		this.localStreams.forEach(function (stream) {
			stream.getAudioTracks().forEach(function (track) {
				enabled = enabled && track.enabled;
			});
		});
		return enabled;
	}

	get videoEnabled() {
		var enabled = true;
		this.localStreams.forEach(function (stream) {
			stream.getVideoTracks().forEach(function (track) {
				enabled = enabled && track.enabled;
			});
		});
		return enabled;
	}
}


export default class MediaManager {
	on(name, cb) {
		return eventHandler.$on(name, cb);
	}
	emit(name, data) {
		return eventHandler.$emit(name, data);
	}
	once(name, cb) {
		return eventHandler.$once(name, cb);
	}
	off(name, cb) {
		return eventHandler.$off(name, cb);
	}

	constructor(opts) {
		if (!webrtcsupport.support) {
			throw new Error('Your browser doesn\'t seem to support WebRTC');
		}

		this.pc = null;

		// Right now, browsers only support a single stream per PeerConnection.
		// That may change in the future, but it simplifies things at the moment.
		this.remoteStream = null;

		this.config = {
			localMediaEl: '',
			remoteMediaEl: '',
			// Which kind of local media to use
			media: {
				video: false,
				audio: true
			},
			autoRequestMedia: false,
			receiveMedia: {
				offerToReceiveAudio: true,
				offerToReceiveVideo: true
			},
			peerConnectionConstraints: {
				optional: [
					{ DtlsSrtpKeyAgreement: true }
				]
			},
		};

		// set our config from options
		opts = opts || {};
		for (var item in opts) {
			if (opts.hasOwnProperty(item)) {
				this.config[item] = opts[item];
			}
		}

		this.localStream = new LocalMedia({media: this.config.media});

		// log all events in debug mode
		this.on('*', (event_name, ...event_data) => {
			console.log({type: 'webrtc', name: event_name, data: event_data});
		});

		// message from server with list of STUN and TURN (ICE) servers
		this.on('controller.message.ICEServers', (message) =>{
			// WebRTC libjingle library uses the different field names from browser.
			// need to perform the translation between these formats
			let iceServers = message.list.map((server) => {
				return {
					url : server.uri,
					username : server.username,
					credential : server.password
				};
			});

			this.emit('ICEServersReceived', iceServers);
		});

		this.on('controller.message.answer', (message) => {
			if (this.pc) {
				this.pc.handleAnswer(message, (err) => {
					if (err) {
						/* TODO: Error handling */
					}
				});
			}
		});

		this.on('controller.message.iceCandidate', (message) => {
			if (this.pc) {
				this.pc.processIce({ candidate: message /*{
					candidate: message.candidate,
					sdpMid: message.sdpMid,
					sdpMLineIndex: message.sdpMLineIndex,
				}*/});
			}
		});

		if (this.config.autoRequestMedia) {
			this.startLocalMedia();
		}

		this.on('webrtc.easy_connect', (callback) => {
			// Do all of the steps for connection, then call the callback.
			// Note that these are defined in the reverse order from how they will be executed.

			var step7_do_callback = () => {
				callback();
			};

			var step6_wait_for_ice_completion = () => {
				var cb = () => {
					if (this.pc) {
						if (this.pc.iceConnectionState === 'connected' || this.pc.iceConnectionState === 'completed') {
							this.off('iceConnectionStateChange', cb);
							step7_do_callback();
						}
					}
				};
				this.on('iceConnectionStateChange', cb);
			};

			var step5_send_offer_message = () => {
				step6_wait_for_ice_completion();
				this.start();
			};

			var step4_connect_localmedia_to_peerconnection = () => {
				// Right now, we auto-add the localmedia when we create the
				// PC, so we don't actually need to do it here.
				// This function is left for history.

				step5_send_offer_message();

				// NOTE: step numbers have changed since this block was commented
				//if (this.remoteStream) {
				//	step4_send_offer_message();
				//} else {
				//	this.once('videoAdded', () => {
				//		step4_send_offer_message();
				//	});
				//	// Right now, we assume there can only be one stream
				//	this.pc.addStream(this.localStream.streams[0]);
				//}
			};

			var step3_get_peerconnection = (iceServers) => {
				if (! this.pc) {
					this.pc = createPeer.call(this, iceServers);
				}
				step4_connect_localmedia_to_peerconnection();
			};

			var step2_request_ice_servers = () => {
				// request ICE (STUN, TURN) servers from server
				this.once('ICEServersReceived', step3_get_peerconnection);
				this.emit('controller.send_ice_servers_request', new ICEServersRequestMessage());
			};

			var step1_get_media = () => {
				if (this.localStream.streams.length > 0) {
					step2_request_ice_servers();
				} else {
					this.once('haveLocalMedia', step2_request_ice_servers);
					this.startLocalMedia();
				}
			};

			// Now start the process
			step1_get_media();
		});

		this.on('hangup', () => {
			this.end();
			this.stopLocalMedia();
		});
	}

	mute() {
		return this.localStream.mute();
	}
	unmute() {
		return this.localStream.unmute();
	}
	pauseVideo() {
		return this.localStream.pauseVideo();
	}
	resumeVideo() {
		return this.localStream.resumeVideo();
	}
	pause() {
		return this.localStream.pause();
	}
	resume() {
		return this.localStream.resume();
	}

	startLocalMedia() {
		this.localStream.start((err, stream) => {
			if (err) {
				this.emit('localMediaError', err);
			} else {
				attachMediaStream(this.localMediaContainer, stream);
				this.emit('haveLocalMedia');
			}
		});
	}

	stopLocalMedia() {
		this.localStream.stop();
	}

	start() {
		if (this.pc) {
			this.pc.offer(this.config.receiveMedia, function () { });
		} else {
			// TODO: ERROR HANDLING
		}
	}
	icerestart() {
		if (this.pc) {
			var constraints = this.config.receiveMedia;
			constraints.mandatory.IceRestart = true;
			this.pc.offer(constraints, function () { });
		} else {
			// TODO: ERROR HANDLING
		}
	}
	end() {
		if (this.pc && !pc_is_closed(this.pc)) {
			this.pc.close();
			this.pc = null;
			// TODO: DRY this
			this.remoteStream = null;
			var videoEl = this.remoteVideo;
			if (videoEl) {
				videoEl.src = '';
				this.remoteVideo = null;
			}
			this.emit('videoRemoved');
		}
	}

	// this accepts either element ID or element
	// and either the video tag itself or a container
	// that will be used to put the video tag into.
	get localMediaContainer() {
		var el = getElement(this.config.localMediaEl);
		// Check whether we are using video (with or without audio), or audio only
		if (this.config.media.video) {
			if (el && el.tagName === 'VIDEO') {
				return el;
			} else if (el) {
				var video = document.createElement('video');
				el.appendChild(video);
				return video;
			}
		} else {
			if (el && el.tagName === 'AUDIO') {
				return el;
			} else if (el) {
				var audio = document.createElement('audio');
				el.appendChild(audio);
				return audio;
			}
		}

		return undefined;
	}

	get remoteMediaContainer() {
		return getElement(this.config.remoteMediaEl);
	}
}

// jshint -W040
function createPeer(iceServers) {
	var peerConnectionConfig = { iceServers : iceServers };
	var pc = new PeerConnection(peerConnectionConfig, this.config.peerConnectionConstraints);
	pc.on('ice', (candidate_event) => {
		if (pc_is_closed(pc)) { return; }
		if (candidate_event) {
			if (webrtcsupport.prefix === 'moz' && this.pcConfig && this.pcConfig.iceTransports &&
					candidate_event.candidate && candidate_event.candidate.candidate &&
					candidate_event.candidate.candidate.indexOf(this.pcConfig.iceTransports) < 0) {
				//TODO
			} else {
				var msg = new IceCandidateMessage({
					candidate: candidate_event.candidate.candidate,
					sdpMid: candidate_event.candidate.sdpMid,
					sdpMLineIndex: candidate_event.candidate.sdpMLineIndex || '0',
				});
				this.emit('controller.on_ice_candidate', msg);
			}
		} else {
			//TODO
		}
	});
	pc.on('offer', (offer) => {
		this.emit('controller.on_offer', new OfferSDPMessage(offer));
	});
	pc.on('answer', (answer) => {
		this.emit('controller.on_answer', new AnswerSDPMessage(answer));
	});

	pc.on('addStream', (event) => {
		if (this.remoteStream) {
			this.logger.warn('Already have a remote stream');
		} else {
			this.remoteStream = event.stream;
			// FIXME: addEventListener('ended', ...) would be nicer
			// but does not work in firefox
			this.remoteStream.onended = () => {
				this.end();
			};

			var container = this.remoteMediaContainer;
			this.remoteVideo = attachMediaStream(container, this.remoteStream);

			this.emit('videoAdded', this.remoteVideo);
		}
	});

	pc.on('removeStream', () => {
		this.remoteStream = null;
		var videoEl = this.remoteVideo;
		if (videoEl) {
			videoEl.src = '';
			this.remoteVideo = null;
		}
		this.emit('videoRemoved');
	});

	// Just fire negotiation needed events for now
	// When browser re-negotiation handling seems to work
	// we can use this as the trigger for starting the offer/answer process
	// automatically. We'll just leave it be for now while this stabalizes.
	pc.on('negotiationNeeded', this.emit.bind(this, 'negotiationNeeded'));
	pc.on('iceConnectionStateChange', this.emit.bind(this, 'iceConnectionStateChange'));
	pc.on('iceConnectionStateChange', () => {
		switch (pc.iceConnectionState) {
		case 'failed':
			// currently, in chrome only the initiator goes to failed
			// so we need to signal this to the peer
			if (pc.pc.peerconnection && pc.pc.peerconnection.localDescription.type === 'offer') {
				this.emit('iceFailed', pc);
			}
			break;
		}
	});
	pc.on('signalingStateChange', this.emit.bind(this, 'signalingStateChange'));

	this.localStream.streams.forEach((stream) => {
		pc.addStream(stream);
	});

	return pc;
}
// jshint +W040

function pc_is_closed(pc) {
	return pc.pc && pc.pc.signalingState === 'closed';
}

function getElement(idOrEl) {
	if (typeof idOrEl === 'string') {
		return document.getElementById(idOrEl);
	} else {
		return idOrEl;
	}
}

function attachMediaStream(obj, stream) {
	obj.srcObject = stream;
}
